import { Link } from "@tanstack/react-router";
import { Star, Quote } from "lucide-react";
import { CLIENT_REVIEWS } from "@/lib/clientReviews";

/**
 * Server-rendered Google reviews section. Replaces the Elfsight widget on
 * pages outside of `/reviews` because the Elfsight script costs ~1.6s of
 * main-thread CPU per page load (per GTmetrix) and contributes ZERO SEO
 * value — Google can't index iframe/shadow-DOM content from a third-party
 * domain. Static cards instead:
 *
 *   - Add crawlable, keyword-rich review text to every page (helps SEO + AI)
 *   - Keep the ★ rich snippet via the sitewide `AggregateRating` JSON-LD
 *   - Cost ~0ms of JS execution
 *
 * Users who want the live feed are funneled to `/reviews` where the real
 * Elfsight widget still mounts (lazy-loaded).
 */
export function StaticReviews({
  eyebrow = "Client reviews",
  heading = "What Colorado clients are saying",
  description = "Verified reviews from our Google Business Profile. We hand-pick a few to show here — see the full feed on our reviews page.",
  count = 6,
}: {
  eyebrow?: string;
  heading?: string;
  description?: string;
  count?: number;
}) {
  // Newest reviews first.
  const reviews = [...CLIENT_REVIEWS]
    .sort((a, b) => b.datePublished.localeCompare(a.datePublished))
    .slice(0, count);

  return (
    <section className="bg-background py-16 lg:py-20">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="text-center">
          <span className="inline-block rounded-full border border-brand-gold/40 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold-deep">
            {eyebrow}
          </span>
          <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
            {heading}
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
            {description}
          </p>
          <div className="mt-5 inline-flex items-center gap-2 text-sm text-muted-foreground">
            <span className="flex" aria-label="Five out of five stars">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star
                  key={i}
                  className="h-4 w-4 fill-brand-gold text-brand-gold"
                  aria-hidden="true"
                />
              ))}
            </span>
            <span className="font-semibold text-foreground">5.0</span>
            <span>·</span>
            <span>150+ Google reviews</span>
          </div>
        </div>

        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {reviews.map((r) => (
            <article
              key={`${r.author}-${r.datePublished}`}
              className="relative flex flex-col rounded-2xl border border-border bg-card p-6 shadow-card"
              itemScope
              itemType="https://schema.org/Review"
            >
              <Quote
                className="absolute right-5 top-5 h-8 w-8 text-brand-gold/20"
                aria-hidden="true"
              />
              <div className="flex" aria-label={`${r.rating} out of 5 stars`}>
                {Array.from({ length: r.rating }).map((_, i) => (
                  <Star
                    key={i}
                    className="h-4 w-4 fill-brand-gold text-brand-gold"
                    aria-hidden="true"
                  />
                ))}
              </div>
              <meta itemProp="reviewRating" content={String(r.rating)} />
              <p
                className="mt-4 flex-1 text-sm leading-relaxed text-foreground/85"
                itemProp="reviewBody"
              >
                "{r.body}"
              </p>
              <footer className="mt-5 border-t border-border pt-4">
                <div className="font-semibold text-foreground" itemProp="author">
                  {r.author}
                </div>
                {(r.location || r.service) && (
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {r.location}
                    {r.location && r.service ? " · " : ""}
                    {r.service}
                  </div>
                )}
                <meta itemProp="datePublished" content={r.datePublished} />
              </footer>
            </article>
          ))}
        </div>

        <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link
            to="/reviews"
            className="inline-flex items-center justify-center rounded-md bg-gradient-gold px-7 py-3 text-sm font-bold uppercase tracking-wider text-brand-black shadow-gold transition-smooth hover:-translate-y-0.5"
          >
            See all 150+ Google reviews
          </Link>
          <a
            href="https://g.page/r/coloradowebimpressions"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center rounded-md border border-border bg-background px-6 py-3 text-sm font-semibold text-foreground transition-smooth hover:border-brand-gold hover:text-brand-gold-deep"
          >
            View on Google
          </a>
        </div>
      </div>
    </section>
  );
}
